import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState
} from "react";
import { DefaultModalForm } from "collections/Modal";
import "./PolyPlusAssignment.scss";
import { LocalKeys, useTranslation } from "utilities/i18n";
import { LoadingIndicator, NoResultsIndicator, PolyIcon } from "elements";
import {
  DevicesLocationQuery,
  EntitleResultStatus,
  useEntitleAllDevicesForModelMutation,
  useEntitleDevicesMutation,
  useLicenseAggregationsByModelQuery
} from "generated/graphql";
import { Link } from "polyrouter";
import { humanReadable } from "utilities/numberUtils";
import { useAccountQuery } from "routes/Admin/Accounts/View/AccountDetails";
import polyPlusLicense from "assets/images/poly-plus-license.svg";
import {
  ProgressStep,
  StepIndicator
} from "collections/StepProgressIndicator/StepProgressIndicator";
import {
  AssignDevices,
  assignDevices,
  AssignmentState,
  assignMethod,
  assignModels,
  assignResults,
  assignStep,
  defaultPolyPlusAssignmentState,
  PolyPlusAssignmentReducer,
  SelectedModel
} from "./PolyPlusAssignmentReducer";
import { generatePath } from "react-router";
import {
  AssignmentDeviceTableView,
  TableViewHeadingProps,
  TableViewSelectedDevice
} from "./PolyPlusAssignmentDevices";
import { HEADER_ID } from "collections/Table";
import { useSearchTerm } from "utilities/search";
import { useDevices } from "routes/Manage/Inventory/List/useDevices";
import { useDeviceCount } from "routes/Manage/Inventory/List/useDeviceCount";
import { DeviceCardProps } from "routes/Manage/Inventory/List/DeviceCard";
import { arraySum } from "utilities/arrayUtils";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

/**
 * This file contains the modal multi-step "Wizard" for assigning Poly Plus licenses to devices.
 *
 * NOTES ON SCHEMA for licenseAggregation:
 * activeLicenseCount: total licenses purchased & not expired - irrespective of whether used or not.
 * availableLicenseCount: total licenses purchased & not expired and not used.
 * expiredLicenseCount:  total count of expired licenses, irrespective of used or not.
 *
 * eligibleDeviceCount: eligible to be licensed - irrespective of assigned or not.
 * availableDeviceCount: total devices in inventory that are eligible to be licensed and not yet assigned.
 * entitledDeviceCount:  total devices (model) in inventory that are assigned licenses.
 */
interface AssignComponentProps {
  tenant: any;
}

interface AssignNavProps {
  nextLabelKey: LocalKeys | "";
  disabled: boolean;
  processing?: boolean;
}

const AssignmentContext = createContext<[AssignmentState, React.Dispatch<any>]>(
  [defaultPolyPlusAssignmentState, () => null]
);

// Small component to simplify context provider usage
const AssignmentProvider: React.FC = props => {
  const [state, dispatch] = useReducer(
    PolyPlusAssignmentReducer,
    defaultPolyPlusAssignmentState
  );

  return <AssignmentContext.Provider value={[state, dispatch]} {...props} />;
};

// Hook to simplify using context in sub-components
export const useAssignmentContext = () => {
  return useContext(AssignmentContext);
};

// Parent to the Modal, so modal and all sub-components have access to context
export const PolyPlusAssignFlow: React.FC = () => {
  return (
    <AssignmentProvider>
      <PolyPlusAssignModal />
    </AssignmentProvider>
  );
};

const extractSerialsFromSelected = (
  selectedDevices: Array<TableViewSelectedDevice> | undefined
) => {
  if (!selectedDevices) {
    return [];
  }

  return selectedDevices
    .filter(device => device.id !== HEADER_ID)
    .map(device => device.serial || "")
    .filter(serial => !!serial);
};

/**
 * Parent container.
 * Provides sizing, layout, close icon, etc.
 *
 * Also provides logic "flow" for the content in the right pane
 * (which is rendered via sub-components)
 */
const PolyPlusAssignModal: React.FC = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useAssignmentContext();
  const { currentStep, method, selectedModels, selectedDevices } = state;

  const tenantId = useTenantId();
  const { tenant } = useAccountQuery(tenantId);

  const onClose = () => {};

  const assignSubTitle = method ? t(method) : "";
  const modelSubTitle = selectedModels.length ? selectedModels[0].name : "";
  const countDevices = extractSerialsFromSelected(selectedDevices).length;
  const countDevicesFormatted = humanReadable(countDevices, true, true)
    .hrOutput;
  const countSubTitle = countDevices
    ? t("POLY_PLUS_ASSIGNMENT_COUNT", { countDevices: countDevicesFormatted })
    : "";

  const manualSteps: Array<ProgressStep> = [
    { title: t("ASSIGNMENT_METHOD"), subtitle: assignSubTitle },
    { title: t("SELECT_MODEL"), subtitle: modelSubTitle },
    { title: t("ASSIGN_ENTITLEMENTS"), subtitle: countSubTitle },
    { title: t("REVIEW_INFORMATION") }
  ];

  const automaticSteps: Array<ProgressStep> = [
    { title: t("ASSIGNMENT_METHOD"), subtitle: assignSubTitle },
    { title: t("REVIEW_INFORMATION") }
  ];

  let steps = "AUTOMATIC" === method ? automaticSteps : manualSteps;

  if (currentStep < 1) {
    steps = steps.map((step, index) => {
      return {
        title: index ? "" : step.title,
        subtitle: index ? "" : step.subtitle
      };
    });
  }

  /**
   * Reset the "method" when navigate back to first step.
   */
  useEffect(() => {
    if (!currentStep && method) {
      dispatch(assignMethod(""));
    }
  }, [currentStep, method, dispatch]);

  const fadeOut = !method;

  const STEPS = {
    AUTOMATIC: {
      CONFIRM: 1,
      DONE: 2
    },
    MANUAL: {
      CHOOSE_MODEL: 1,
      CHOOSE_DEVICES: 2,
      CONFIRM: 3,
      DONE: 4
    }
  };

  return (
    <DefaultModalForm
      onSubmit={onClose}
      name="assignLicenses"
      title=""
      className="full assign-entitlements"
      closeIcon={true}
    >
      <h5 className="text-uppercase">{t("ASSIGN_YOUR_ENTITLEMENTS")}</h5>
      <div className="grid select-container">
        <div className="col-3 has-divider">
          <StepIndicator
            steps={steps}
            currentStep={currentStep}
            fadeOut={fadeOut}
          />
        </div>
        <div className="col-9 relative">
          <div className="flex-1">
            {!method && <AssignmentMethod />}
            {"AUTOMATIC" === method && (
              <>
                {STEPS.AUTOMATIC.CONFIRM === currentStep && (
                  <AssignmentConfirmAutomatic tenant={tenant} />
                )}
                {STEPS.AUTOMATIC.DONE === currentStep && <AssignmentDone />}
              </>
            )}
            {"MANUAL" === method && (
              <>
                {STEPS.MANUAL.CHOOSE_MODEL === currentStep && (
                  <AssignmentChooseModel tenant={tenant} />
                )}
                {STEPS.MANUAL.CHOOSE_DEVICES === currentStep && (
                  <AssignmentChooseDevices tenant={tenant} />
                )}
                {STEPS.MANUAL.CONFIRM === currentStep && (
                  <AssignmentConfirmManual tenant={tenant} />
                )}
                {STEPS.MANUAL.DONE === currentStep && <AssignmentDone />}
              </>
            )}
          </div>
        </div>
      </div>
    </DefaultModalForm>
  );
};

const AssignmentMethod: React.FC = () => {
  const { t } = useTranslation();
  const [{ currentStep }, dispatch] = useAssignmentContext();

  return (
    <div className="ph-2">
      <h4 className="mb-3">{t("ENTITLEMENTS_ASSIGNMENT_SELECT_METHOD")}</h4>
      <div
        role="button"
        className="card btn-card clickable"
        onClick={() => {
          dispatch(assignMethod("AUTOMATIC"));
          dispatch(assignStep(currentStep + 1));
        }}
      >
        <h5 className="mb-1 text-uppercase">{t("AUTOMATIC")}</h5>
        <p>{t("ENTITLEMENTS_ASSIGNMENT_AUTOMATIC_DESCRIPTION")}</p>
      </div>
      <div
        role="button"
        className="card btn-card clickable"
        onClick={() => {
          dispatch(assignMethod("MANUAL"));
          dispatch(assignStep(currentStep + 1));
        }}
      >
        <h5 className="mb-1 text-uppercase">{t("MANUAL")}</h5>
        <p>{t("ENTITLEMENTS_ASSIGNMENT_MANUAL_DESCRIPTION")}</p>
      </div>
    </div>
  );
};

export const AssignmentChooseModel: React.FC<AssignComponentProps> = ({
  tenant
}) => {
  const { t } = useTranslation();
  const { id: tenantId } = tenant;
  const { data, fetching } = useLicenseAggregationsByModelQuery({
    variables: { tenantId },
    requestPolicy: "network-only"
  });

  const [state, dispatch] = useAssignmentContext();

  const { selectedModels } = state;
  const selectedModel = selectedModels[0] || { id: null, name: null };

  if (fetching) {
    return <LoadingIndicator />;
  }

  let licenses = data?.licenseAggregationsByModel || [];
  licenses = licenses.filter(
    l => l.availableDeviceCount && l.availableLicenseCount
  );

  if (!licenses.length) {
    return <h5>{t("POLY_PLUS_NO_LICENSES_AVAILABLE")}</h5>;
  }

  const abbreviated = (num: number) => {
    const nums = humanReadable(num, true, true);
    return nums.hrOutput;
  };

  return (
    <div className="ph-2">
      <h4 className="mb-3 text-center">
        {t("ENTITLEMENTS_ASSIGNMENT_SELECT_MODEL")}
      </h4>
      <div className="grid grid-table-xl ph-2 pb-2 scrollable-stretch-height">
        {licenses.map(license => {
          const classes = [
            "row",
            "card",
            "btn-card",
            "mb-0",
            "clickable",
            selectedModel.id === license.id ? "selected" : ""
          ];

          return (
            <div
              className={classes.filter(c => !!c).join(" ")}
              key={license.id}
              onClick={() => {
                dispatch(
                  assignModels([
                    {
                      id: license.id,
                      name: license.name,
                      availableDeviceCount: license.availableDeviceCount,
                      availableLicenseCount: license.availableLicenseCount,
                      entitledDeviceCount: license.entitledDeviceCount
                    }
                  ])
                );
              }}
            >
              <div className="col-6 v-center">
                <ModelName modelName={license.name} />
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("PURCHASED")}</div>
                {abbreviated(license.activeLicenseCount)}
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("ASSIGNED")}</div>
                {abbreviated(license.entitledDeviceCount)}
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("AVAILABLE")}</div>
                {abbreviated(license.availableLicenseCount)}
              </div>
            </div>
          );
        })}
      </div>
      <AssignNav nextLabelKey="NEXT" disabled={!selectedModel.id} />
    </div>
  );
};

export const AssignmentTableHeading: React.FC<TableViewHeadingProps> = ({
  selectedDeviceIDs
}) => {
  const { t } = useTranslation();
  const [state] = useAssignmentContext();

  const availableLicenses = useMemo(() => {
    return arraySum(state.selectedModels, "availableLicenseCount");
  }, [state.selectedModels]);
  const selectedCount = (selectedDeviceIDs || []).length;
  const tooMany = selectedCount > availableLicenses;
  const selectedClasses = [
    "font-xxl",
    "ml-1",
    selectedCount && selectedCount <= availableLicenses ? "font-green" : "",
    tooMany ? " font-red" : ""
  ]
    .filter(c => !!c)
    .join(" ");

  return (
    <>
      <span className="font-xxl">{availableLicenses}</span>
      <span className="font-bold text-uppercase letter-space-1 ml-5">
        {t("AVAILABLE_LICENSES")}
      </span>
      <span className={selectedClasses} data-testid="selected-device-count">
        {selectedCount}
      </span>
      <span className="font-bold text-uppercase letter-space-1 ml-5">
        {t("SELECTED")}
      </span>
      {tooMany && (
        <p className="font-red">{t("ASSIGNMENT_TOO_MANY_SELECTED")}</p>
      )}
    </>
  );
};

const nowStamp = new Date().toISOString();

const AssignmentChooseDevices: React.FC<AssignComponentProps> = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useAssignmentContext();
  const { selectedDevices, selectedModels, method } = state;
  const selectedModel = selectedModels[0];

  const [searchTerm] = useSearchTerm();
  const [cursor, setCursor] = useState(0);
  // to disable the "next" button if more licenses selected than available
  const availableLicenses = useMemo(() => {
    return arraySum(selectedModels, "availableLicenseCount");
  }, [selectedModels]);
  const tooManySelected =
    method === "MANUAL" && selectedDevices.length > availableLicenses;

  const { data, fetching, stale, executor } = useDevices(
    false,
    searchTerm ?? undefined,
    {
      model: selectedModel.name,
      entitledUntilDate: { OR: [{ exists: false }, { lt: String(nowStamp) }] }
    },
    cursor,
    "network-only"
  );

  // total device counts without any filters applied
  // need this count to decide whether to show empty state for devices
  const { count: totalCount, fetching: countFetching } = useDeviceCount();

  let devices: Array<DeviceCardProps> = [];
  let aggregates: DevicesLocationQuery["deviceAggregations"] = [];
  let count = 0;
  if (data && data.deviceList) {
    devices = data.deviceList;
    count = data.deviceCount;
    aggregates = data.deviceAggregations;
  }

  const assignSelected = useCallback(
    (devices: AssignDevices) => {
      dispatch(assignDevices(devices));
    },
    [dispatch]
  );

  return (
    <div className="ph-2">
      <h4 className="text-center mb-15">
        {t("POLY_PLUS_ENTITLEMENT_SELECT_DEVICES")}
      </h4>
      <h3 className="text-uppercase letter-space-1 mb-1">
        {selectedModel.name}
      </h3>
      <AssignmentDeviceTableView
        headingChildren={<AssignmentTableHeading />}
        setCursor={setCursor}
        devices={devices}
        aggregates={aggregates}
        loading={fetching || countFetching}
        stale={stale}
        count={count}
        totalCount={totalCount}
        refreshDevices={executor}
        setSelected={assignSelected}
        tableKey="POLY_PLUS_ENTITLE_DEVICES"
      />
      <AssignNav
        nextLabelKey="NEXT"
        disabled={!selectedDevices.length || tooManySelected}
      />
    </div>
  );
};

const AssignmentConfirmAutomatic: React.FC<AssignComponentProps> = ({
  tenant
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useAssignmentContext();
  const { currentStep } = state;
  const [, entitleAllDevices] = useEntitleAllDevicesForModelMutation();
  const [processing, setProcessing] = useState(false);
  const { id: tenantId } = tenant;
  const { data, fetching } = useLicenseAggregationsByModelQuery({
    variables: { tenantId },
    requestPolicy: "network-only"
  });

  const eligibleLicenses =
    data && data.licenseAggregationsByModel
      ? data.licenseAggregationsByModel.filter(
          l => l.availableDeviceCount && l.availableLicenseCount
        )
      : [];

  useEffect(() => {
    if (data) {
      const models = eligibleLicenses
        .filter(l => l.availableDeviceCount && l.availableLicenseCount)
        .map(license => ({
          id: license.id,
          name: license.name,
          availableDeviceCount: license.availableDeviceCount,
          availableLicenseCount: license.availableLicenseCount,
          entitledDeviceCount: license.entitledDeviceCount
        }));
      dispatch(assignModels(models));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, data]);

  if (fetching) {
    return <LoadingIndicator />;
  }

  if (!eligibleLicenses.length) {
    return <NoResultsIndicator />;
  }

  const assign = async () => {
    setProcessing(true);

    const results = await Promise.all(
      eligibleLicenses.map(license => {
        const { id: modelId } = license;

        return entitleAllDevices({
          tenantId,
          modelId
        });
      })
    );

    const successCount: Array<string> = [];
    const errors: Array<{ modelId: string; error: EntitleResultStatus }> = [];

    eligibleLicenses.forEach((license, i) => {
      const result = results[i];
      const { id: modelId } = license;
      // Data is not always returned for all models requested
      if (!result?.data?.entitleAllDevicesForModel?.length) {
        errors.push({ modelId, error: EntitleResultStatus.LicenseNotFound });
        return;
      }

      result?.data?.entitleAllDevicesForModel.forEach(({ resultCode }) => {
        if (resultCode === EntitleResultStatus.Success) {
          successCount.push();
        }

        if (resultCode !== EntitleResultStatus.Success) {
          errors.push({ modelId, error: resultCode });
        }
      });
    });

    setProcessing(false);
    dispatch(assignResults({ assigned: successCount, errors }));
    dispatch(assignStep(currentStep + 1));
  };

  const nextLabelKey = !processing ? "CONFIRM" : "SAVING_ENTITLEMENTS";

  return (
    <>
      <h4 className="text-center mb-4">{t("POLY_PLUS_CONFIRM_INFORMATION")}</h4>
      <div className="grid grid-table-xl ph-2 pb-2 scrollable-stretch-height">
        {eligibleLicenses.map(license => {
          const model: SelectedModel = { ...license };
          // Do count here of number selected for this model...
          const assigned = license.availableLicenseCount;
          return (
            <AssignmentGridModel
              key={model.id}
              selectedModel={model}
              assigned={assigned}
            />
          );
        })}
      </div>
      <div className="pt-2 text-center">
        <button
          className="btn"
          onClick={() => dispatch(assignStep(currentStep - 1))}
        >
          {t("BACK")}
        </button>
        <button
          className="btn btn-primary"
          onClick={assign}
          disabled={processing}
        >
          {processing && <PolyIcon icon="refresh" className="spin mr-5" />}
          {t(nextLabelKey)}
        </button>
      </div>
    </>
  );
};

const AssignmentConfirmManual: React.FC<AssignComponentProps> = ({
  tenant
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useAssignmentContext();
  const { currentStep, selectedModels, selectedDevices } = state;
  const selectedModel = selectedModels[0];
  const [processing, setProcessing] = useState(false);
  const [, entitleDevices] = useEntitleDevicesMutation();
  const { id: tenantId } = tenant;

  const assign = async () => {
    setProcessing(true);

    const results = await entitleDevices({
      tenantId,
      modelId: selectedModel.id,
      productSerials: extractSerialsFromSelected(selectedDevices)
    });

    const successCount: Array<string> = [];
    const errors: Array<{ modelId: string; error: EntitleResultStatus }> = [];

    results?.data?.entitleDevices.forEach(({ resultCode }) => {
      if (resultCode === EntitleResultStatus.Success) {
        successCount.push(selectedModel.id);
      }

      if (resultCode !== EntitleResultStatus.Success) {
        errors.push({ modelId: selectedModel.id, error: resultCode });
      }
    });

    setProcessing(false);
    dispatch(
      assignResults({
        assigned: successCount,
        errors
      })
    );

    dispatch(assignStep(currentStep + 1));
  };

  const nextLabelKey = !processing ? "CONFIRM" : "SAVING_ENTITLEMENTS";

  return (
    <>
      <h4 className="text-center mb-4">{t("POLY_PLUS_CONFIRM_INFORMATION")}</h4>
      <div className="grid grid-table-xl ph-2">
        <AssignmentGridModel
          selectedModel={selectedModel}
          assigned={selectedDevices.length}
        />
      </div>
      <div className="pt-2 text-center">
        <button
          className="btn"
          onClick={() => dispatch(assignStep(currentStep - 1))}
        >
          {t("BACK")}
        </button>
        <button
          className="btn btn-primary"
          onClick={assign}
          disabled={processing}
        >
          {processing && <PolyIcon icon="refresh" className="spin mr-5" />}
          {t(nextLabelKey)}
        </button>
      </div>
    </>
  );
};

const AssignmentGridModel: React.FC<{
  selectedModel: SelectedModel;
  assigned: number;
}> = ({ selectedModel, assigned }) => {
  const { t } = useTranslation();
  const abbreviated = (num: number) => {
    const nums = humanReadable(num, true, true);
    return nums.hrOutput;
  };

  const assigningNow = Math.min(assigned, selectedModel.availableDeviceCount);
  const totalAssigned = selectedModel.entitledDeviceCount + assigningNow;

  return (
    <div className="row card btn-card not-clickable mb-0">
      <div className="col-6 v-center">
        <ModelName modelName={selectedModel.name} />
      </div>
      <div className="col-2 font-bold text-center">
        <div className="value-title">{t("TOTAL_LICENSES")}</div>
        {abbreviated(selectedModel.availableLicenseCount)}
      </div>
      <div className="col-2 font-bold text-center">
        <div className="value-title">{t("ASSIGNING_NOW")}</div>
        <span className="font-green">{abbreviated(assigningNow)}</span>
      </div>
      <div className="col-2 font-bold text-center">
        <div className="value-title">{t("TOTAL_ASSIGNED")}</div>
        {abbreviated(totalAssigned)}
      </div>
    </div>
  );
};

const AssignmentDone: React.FC = () => {
  const [state] = useAssignmentContext();
  const { assigned, errors } = state;

  if (assigned && !errors.length) {
    return <AssignmentSuccess />;
  }

  return <AssignmentErrors />;
};

/**
 * Success message and gif.
 * Rendered if all / 100% of devices were entitled successfully.
 * (Otherwise, AssignmentErrors component is rendered).
 */
const AssignmentSuccess: React.FC = () => {
  const { t } = useTranslation();
  const url = generatePath("/manage/licensing");

  return (
    <div className="text-center">
      <h4>{t("POLY_PLUS_CONFIRMATION")}</h4>
      <div className="mt-3 mb-2">
        <img src={polyPlusLicense} alt={t("POLY_PLUS_LICENSE")} />
      </div>
      <Link to={url} className="btn">
        {t("DONE")}
      </Link>
    </div>
  );
};

/**
 * Error message and list of devices.
 * Rendered if ANY of the devices were not entitled successfully.
 */
const AssignmentErrors: React.FC = () => {
  const [state] = useAssignmentContext();

  // Errors is an array of {modelId, error}
  // assigned is an array of modelId (represent "success" assignments)
  const { errors, assigned, selectedModels } = state;
  const { t } = useTranslation();
  const url = generatePath("/manage/licensing");

  const abbreviated = (num: number) => {
    const nums = humanReadable(num, true, true);
    return nums.hrOutput;
  };

  const initial: { [key: string]: number } = {};
  const errorCountByModel = errors.reduce(
    (acc, err) => {
      const { modelId } = err;
      if (!acc[modelId]) {
        acc[modelId] = 0;
      }

      return { ...acc, [modelId]: acc[modelId] + 1 };
    },
    { ...initial }
  );

  const shipDateErrorByModel = errors.reduce(
    (acc, err) => {
      if (err.error !== EntitleResultStatus.ShipmentTooOld) {
        return acc;
      }

      const { modelId } = err;
      if (!acc[modelId]) {
        acc[modelId] = 0;
      }

      return { ...acc, [modelId]: acc[modelId] + 1 };
    },
    { ...initial }
  );

  // reduce to an object of errors by model ID: {[key: modelId]: number}
  const successCountByModel = assigned.reduce(
    (acc, modelId) => {
      if (!acc[modelId]) {
        acc[modelId] = 0;
      }

      return { ...acc, [modelId]: acc[modelId] + 1 };
    },
    { ...initial }
  );

  const subTitleKey = assigned.length
    ? "POLY_PLUS_ENTITLE_SUCCESS_SOME"
    : "POLY_PLUS_ENTITLE_SUCCESS_NONE";

  const displayErrors: Array<string> = [];
  if (Object.keys(shipDateErrorByModel).length) {
    Object.keys(shipDateErrorByModel).forEach(modelId => {
      const errorModel = selectedModels.find(m => m.id === modelId);
      const model = errorModel ? errorModel.name : t("UNKNOWN");
      const count = shipDateErrorByModel[modelId];
      displayErrors.push(
        t("POLY_PLUS_ENTITLE_ERROR_SHIP_DATE_OLD", { count, model })
      );
    });
  }

  return (
    <div className="text-center">
      <h4>{t("POLY_PLUS_SOMETHING_WENT_WRONG")}</h4>
      <p>{t(subTitleKey)}</p>
      <div className="grid grid-table-xl mt-2 pb-2 ph-2 pb-2 scrollable-stretch-height">
        {!!displayErrors.length && (
          <div className="banner banner-error col-8 offset-2 mb-2">
            <div className="text-left font-sm">
              {displayErrors.map(error => (
                <div className="pb-15" key={error}>
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
        {selectedModels.map(selectedModel => {
          const unsuccessful = errorCountByModel[selectedModel.id] || 0;
          const assigned = successCountByModel[selectedModel.id] || 0;

          return (
            <div key={selectedModel.id} className="row btn-card mb-1">
              <div className="col-4 v-center">
                <ModelName modelName={selectedModel.name} />
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("LICENSES")}</div>
                {abbreviated(selectedModel.availableLicenseCount)}
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("ASSIGNED")}</div>
                {abbreviated(assigned)}
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">{t("UNSUCCESSFUL")}</div>
                {abbreviated(unsuccessful)}
              </div>
              <div className="col-2 font-bold text-center">
                <div className="value-title">Unlicensed</div>
                {abbreviated(selectedModel.availableLicenseCount)}
              </div>
            </div>
          );
        })}
      </div>
      <Link to={url} className="btn">
        {t("DONE")}
      </Link>
    </div>
  );
};

const AssignNav: React.FC<AssignNavProps> = ({
  nextLabelKey,
  disabled,
  processing
}) => {
  const { t } = useTranslation();

  const [state, dispatch] = useAssignmentContext();
  const { currentStep } = state;

  const setStep = (step: number) => {
    dispatch(assignStep(step));
  };

  return (
    <div className="pt-2 text-center">
      <button
        className="btn"
        onClick={() => setStep(currentStep - 1)}
        disabled={processing}
      >
        {t("BACK")}
      </button>
      {nextLabelKey && (
        <button
          className="btn btn-primary"
          onClick={() => setStep(currentStep + 1)}
          disabled={disabled || processing}
        >
          {processing && <PolyIcon icon="refresh" className="spin mr-5" />}
          {t(nextLabelKey)}
        </button>
      )}
    </div>
  );
};

const ModelName: React.FC<{
  modelName: string;
  Tag?: keyof JSX.IntrinsicElements;
  className?: string;
}> = ({ modelName, Tag = "div", className }) => {
  const classes = [
    "text-left mid-gray font-xl letter-space-1 text-uppercase",
    className
  ]
    .filter(c => !!c)
    .join(" ");
  return <Tag className={classes}>{modelName}</Tag>;
};
